import { Box, Tooltip, Typography } from '@mui/material';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import { visuallyHidden } from '@mui/utils';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import NoData from '../../../components/NoData';
import PathCircularProgress from '../../../components/PathCircularProgress';

import { styled } from '@mui/material/styles';
import {
  MenuItemData,
  ThreeDotsMenu,
} from '../../../components/documented/ThreeDotsMenu';
import SearchBar from '../../../components/SearchBar/SearchBar';
import { PendingPatient } from '../../../model/patient/pendingPatient';
import { cancelPendingPatientAuthorisation } from '../../../redux/actions/healthcareProfessionalActions';
import { useAppThunkDispatch } from '../../../redux/configureStore';
import { selectAccount } from '../../../redux/selectors/accountSelectors';
import { selectPendingPatients } from '../../../redux/selectors/patientSelectors';
import {
  copyIdToClipboard,
  getComparator,
  Order,
  StyledTableCell,
  StyledTableRow,
} from '../TableComponents/TableComponents';

const TableDetailContainer = styled(Box)(() => ({
  height: 'fit-content',
  width: '100%',
  padding: '10px 20px 10px 20px',
}));

export interface HeadCell {
  id: keyof PendingPatient;
  label: string;
  numeric: boolean;
  toolTip?: string;
}

export interface EnhancedTableProps {
  onRequestSort: (
    event: React.MouseEvent<unknown>,
    property: keyof PendingPatient
  ) => void;
  order: Order;
  orderBy: string;
}

function PendingPatientsTableHead(props: EnhancedTableProps) {
  const { order, orderBy, onRequestSort } = props;
  const { t } = useTranslation(['patients', 'common']);
  const createSortHandler =
    (property: keyof PendingPatient) => (event: React.MouseEvent<unknown>) => {
      onRequestSort(event, property);
    };

  const headCells: readonly HeadCell[] = [
    {
      id: 'patientAccountGuid',
      numeric: false,
      label: t('pending.accountGuid'),
    },
    {
      id: 'date',
      numeric: false,
      label: t('pending.requestDate'),
    },
  ];

  return (
    <TableHead>
      <TableRow>
        {headCells.map((headCell) => (
          <StyledTableCell
            key={headCell.id}
            align="left"
            sortDirection={orderBy === headCell.id ? order : false}
          >
            <Tooltip title={headCell.toolTip || ''}>
              <TableSortLabel
                active={orderBy === headCell.id}
                direction={orderBy === headCell.id ? order : 'asc'}
                onClick={createSortHandler(headCell.id)}
              >
                {headCell.label}
                {orderBy === headCell.id ? (
                  <Box component="span" sx={visuallyHidden}>
                    {order === 'desc'
                      ? t('common:table.sortedDescending')
                      : t('common:table.sortedAscending')}
                  </Box>
                ) : null}
              </TableSortLabel>
            </Tooltip>
          </StyledTableCell>
        ))}
        <StyledTableCell>{t('common:table.userActions')}</StyledTableCell>
      </TableRow>
    </TableHead>
  );
}

interface Props {
  isLoading: boolean;
  onRefreshPatients: () => void;
}

export const PendingPatientsTable: React.FC<Props> = ({
  isLoading,
  onRefreshPatients,
}) => {
  const { t } = useTranslation(['patients', 'common']);
  const account = useSelector(selectAccount);
  const pendingPatients = useSelector(selectPendingPatients);
  const [order, setOrder] = React.useState<Order>('asc');
  const [orderBy, setOrderBy] = React.useState<keyof PendingPatient>('date');
  const [searchTerm, setSearchTerm] = React.useState('');
  const dispatch = useAppThunkDispatch();

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
  };

  const filteredPendingPatients = React.useMemo(() => {
    try {
      // If no search term, return all pending patients
      if (!searchTerm) return pendingPatients;

      // Validate patients array
      if (
        !pendingPatients ||
        !Array.isArray(pendingPatients) ||
        pendingPatients.length === 0
      ) {
        return [];
      }

      // Get lowercase search term
      const searchLower = String(searchTerm).toLowerCase();

      // Filter patients with error handling for each one
      return pendingPatients.filter((patient) => {
        try {
          // Return false for any null or undefined patient
          if (!patient) return false;

          // Get patient properties safely
          const patientId =
            typeof patient.patientAccountGuid === 'string'
              ? patient.patientAccountGuid.toLowerCase()
              : '';

          // Check if search term is in any of the fields
          return patientId.includes(searchLower);
        } catch (err) {
          console.error('Error filtering pending patient:', err);
          return false;
        }
      });
    } catch (err) {
      console.error('Error filtering pending patients:', err);
      return [];
    }
  }, [pendingPatients, searchTerm]);

  const handleRequestSort = (
    event: React.MouseEvent<unknown>,
    property: keyof PendingPatient
  ) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleRemoveRequest = (patientId: string) => {
    dispatch(
      cancelPendingPatientAuthorisation(account.accountGuid, patientId)
    ).then((response: any) => {
      onRefreshPatients();
    });
  };

  const menuItems: MenuItemData[] = [
    {
      label: t('common:actions.copyGuid'),
      action: (data) => copyIdToClipboard(data.patientId),
    },
    {
      label: t('common:actions.remove'),
      action: (data) => handleRemoveRequest(data.patientId),
    },
  ];

  return (
    <TableDetailContainer>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 2,
        }}
      >
        <Typography
          sx={{ padding: '5px 0px 5px 0px' }}
          variant="h5"
          component="h2"
        >
          {t('pending.title')}
        </Typography>

        {!isLoading && pendingPatients && pendingPatients.length > 0 && (
          <SearchBar
            placeholder={t('pending.searchPlaceholder')}
            value={searchTerm}
            onChange={handleSearchChange}
          />
        )}
      </Box>
      {!isLoading ? (
        pendingPatients &&
        pendingPatients.length > 0 &&
        filteredPendingPatients.length > 0 ? (
          <Paper sx={{ width: '100%', mb: 2 }}>
            <TableContainer sx={{ maxHeight: 980, minWidth: 750 }}>
              <Table sx={{ minWidth: 750 }} aria-labelledby="tableTitle">
                <PendingPatientsTableHead
                  order={order}
                  orderBy={orderBy}
                  onRequestSort={handleRequestSort}
                />
                <TableBody>
                  {filteredPendingPatients
                    .slice()
                    .sort(getComparator(order, orderBy))
                    .map((patient) => {
                      return (
                        <StyledTableRow
                          hover
                          tabIndex={-1}
                          key={patient.patientAccountGuid}
                        >
                          <StyledTableCell align="left">
                            {patient.patientAccountGuid}
                          </StyledTableCell>
                          <StyledTableCell align="left">
                            {new Date(patient.date).toLocaleString('en-US', {
                              weekday: 'short', // Mon
                              year: 'numeric', // 2024
                              month: 'short', // Oct
                              day: '2-digit', // 14
                              hour: '2-digit', // HH
                              minute: '2-digit', // MM
                              hour12: false, // 24-hour format
                            })}
                          </StyledTableCell>
                          <StyledTableCell align="left">
                            <ThreeDotsMenu
                              data={{
                                patientId: patient.patientAccountGuid,
                                accountId: account.accountGuid,
                              }}
                              menuItems={menuItems}
                            />
                          </StyledTableCell>
                        </StyledTableRow>
                      );
                    })}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        ) : pendingPatients &&
          pendingPatients.length > 0 &&
          filteredPendingPatients.length === 0 ? (
          <NoData
            message={t('pending.noneMatching', { term: searchTerm })}
          />
        ) : (
          <NoData message={t('pending.empty')} />
        )
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', height: '100px', justifyContent: 'center' }}>
          <PathCircularProgress />
        </Box>
      )}
    </TableDetailContainer>
  );
};

export default PendingPatientsTable;
